import { Input, theme } from 'antd';
import type { TextAreaProps as AntTextAreaProps } from 'antd/es/input';
import styled, { css } from 'styled-components';
import type { GlobalToken } from 'antd';

const { TextArea: AntTextArea } = Input;

export type TextAreaStatus = 'default' | 'warning' | 'error' | 'success';

export interface TextAreaProps extends Omit<AntTextAreaProps, 'status'> {
  /** TextArea status for validation feedback */
  status?: TextAreaStatus;
  /** Label text */
  label?: string;
  /** Help text below the textarea */
  helpText?: string;
  /** Whether the field is required */
  required?: boolean;
  /** Show character count */
  showCount?: boolean;
  /** Maximum character length */
  maxLength?: number;
}

const getStatusStyles = (token: GlobalToken, status: TextAreaStatus) => {
  const statusMap = {
    default: css`
      background: ${token.colorBgContainer};
      border-color: ${token.colorBorder};

      &:hover:not(:disabled) {
        border-color: ${token.colorPrimaryHover};
      }

      &:focus,
      &.ant-input-focused {
        border-color: ${token.colorPrimary};
        box-shadow: 0 0 0 2px ${token.colorPrimaryBg};
      }
    `,
    warning: css`
      background: ${token.colorWarningBg};
      border-color: ${token.colorWarning};

      &:hover:not(:disabled) {
        border-color: ${token.colorWarningHover};
      }

      &:focus,
      &.ant-input-focused {
        border-color: ${token.colorWarning};
        box-shadow: 0 0 0 2px ${token.colorWarningBg};
      }
    `,
    error: css`
      background: ${token.colorErrorBg};
      border-color: ${token.colorError};

      &:hover:not(:disabled) {
        border-color: ${token.colorErrorHover};
      }

      &:focus,
      &.ant-input-focused {
        border-color: ${token.colorError};
        box-shadow: 0 0 0 2px ${token.colorErrorBg};
      }
    `,
    success: css`
      background: ${token.colorSuccessBg};
      border-color: ${token.colorSuccess};

      &:hover:not(:disabled) {
        border-color: ${token.colorSuccessHover};
      }

      &:focus,
      &.ant-input-focused {
        border-color: ${token.colorSuccess};
        box-shadow: 0 0 0 2px ${token.colorSuccessBg};
      }
    `,
  };
  return statusMap[status];
};

interface StyledProps {
  $token: GlobalToken;
}

const Wrapper = styled.div<StyledProps>`
  display: flex;
  flex-direction: column;
  gap: ${({ $token }) => $token.marginXXS}px;
  width: 100%;
`;

const LabelWrapper = styled.label<StyledProps>`
  display: flex;
  align-items: center;
  gap: ${({ $token }) => $token.marginXXS}px;
  font-family: ${({ $token }) => $token.fontFamily};
  font-size: ${({ $token }) => $token.fontSize}px;
  font-weight: 400;
  color: ${({ $token }) => $token.colorText};
`;

const RequiredMark = styled.span<StyledProps>`
  color: ${({ $token }) => $token.colorError};
`;

const Footer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const getHelpTextColor = (token: GlobalToken, status: TextAreaStatus) => {
  switch (status) {
    case 'error':
      return token.colorError;
    case 'warning':
      return token.colorWarning;
    case 'success':
      return token.colorSuccess;
    default:
      return token.colorTextTertiary;
  }
};

const HelpText = styled.span<StyledProps & { $status: TextAreaStatus }>`
  font-family: ${({ $token }) => $token.fontFamily};
  font-size: ${({ $token }) => $token.fontSizeSM}px;
  color: ${({ $token, $status }) => getHelpTextColor($token, $status)};
`;

const CharCount = styled.span<StyledProps>`
  font-family: ${({ $token }) => $token.fontFamily};
  font-size: ${({ $token }) => $token.fontSizeSM}px;
  color: ${({ $token }) => $token.colorTextTertiary};
`;

interface StyledTextAreaProps extends StyledProps {
  $status: TextAreaStatus;
}

const StyledTextArea = styled(AntTextArea)<StyledTextAreaProps>`
  font-family: ${({ $token }) => $token.fontFamily};
  font-size: ${({ $token }) => $token.fontSize}px;
  padding: ${({ $token }) => `${$token.paddingXS}px ${$token.paddingSM}px`};
  border-radius: ${({ $token }) => $token.borderRadius}px;
  border: ${({ $token }) => $token.lineWidth}px solid;
  transition: all ${({ $token }) => $token.motionDurationMid} ease;
  resize: vertical;
  min-height: 80px;

  ${({ $token, $status }) => getStatusStyles($token, $status)}

  &:disabled {
    background: ${({ $token }) => $token.colorBgLayout};
    border-color: ${({ $token }) => $token.colorBorderSecondary};
    color: ${({ $token }) => $token.colorTextDisabled};
    cursor: not-allowed;
  }

  &::placeholder {
    color: ${({ $token }) => $token.colorTextTertiary};
  }
`;

/**
 * TextArea component based on Figma Design System
 *
 * Now uses Ant Design theme tokens for consistent styling.
 *
 * @example
 * ```tsx
 * import { TextArea } from '@libertify/ui';
 *
 * <TextArea label="Description" placeholder="Enter description" />
 * <TextArea label="Bio" maxLength={250} showCount />
 * <TextArea status="error" helpText="Description is required" />
 * ```
 */
export const TextArea = ({
  status = 'default',
  label,
  helpText,
  required,
  showCount,
  maxLength,
  value,
  ...props
}: TextAreaProps) => {
  const { token } = theme.useToken();
  const charCount =
    showCount && maxLength
      ? `${String(value || '').length}/${maxLength}`
      : null;

  return (
    <Wrapper $token={token}>
      {label && (
        <LabelWrapper $token={token}>
          {label}
          {required && <RequiredMark $token={token}>*</RequiredMark>}
        </LabelWrapper>
      )}
      <StyledTextArea
        $token={token}
        $status={status}
        maxLength={maxLength}
        value={value}
        {...props}
      />
      {(helpText || charCount) && (
        <Footer>
          {helpText ? (
            <HelpText $token={token} $status={status}>
              {helpText}
            </HelpText>
          ) : (
            <span />
          )}
          {charCount && <CharCount $token={token}>{charCount}</CharCount>}
        </Footer>
      )}
    </Wrapper>
  );
};

export default TextArea;
